import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatIDR, formatDate } from "@/lib/api";
import {
  Wallet, TrendingUp, Clock, ShoppingBag, ChevronDown, ChevronRight,
  ArrowRight, Package, Receipt, Info,
} from "lucide-react";

/**
 * Iter 23 — Jastiper Finance dashboard.
 *
 * Per-order breakdown showing where the money actually flows:
 *
 *   Item A …………………………………………. Rp 250.000  ( + fee Rp 25.000 )
 *   Item B …………………………………………. Rp 120.000  ( + fee 10% )
 *   --------------------------------------
 *   Modal barang (item_total)         Rp 370.000
 *   Cuan jastip (service_fee)         Rp 47.000
 *   Potong platform (-2%)             Rp -7.400
 *   ========================================
 *   PROFIT BERSIH ANDA                Rp 39.600
 *   (Buyer bayar total Rp 380.000, dana yg masuk ke Anda Rp 362.600 — termasuk modal)
 */
export default function JastiperFinance() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(new Set());
  const [tab, setTab] = useState("completed"); // completed | inflight | all

  useEffect(() => {
    api.get("/jastiper/finance")
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, []);

  const toggle = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  if (loading) {
    return <div className="text-center py-20" style={{color: "var(--app-muted)"}} data-testid="finance-loading">Memuat finance...</div>;
  }
  if (!data) return null;

  const totals = data.totals || {};
  const orders = data.orders || [];
  const filtered = tab === "all" ? orders : orders.filter((o) => o.bucket === tab);

  return (
    <div className="space-y-8" data-testid="jastiper-finance-page">
      <div>
        <div className="label-caps mb-2" style={{color: "var(--app-muted)"}}>Finance Jastiper</div>
        <h1 className="font-display text-4xl tracking-tight">Cuan Anda.</h1>
        <p className="text-sm mt-2 max-w-2xl" style={{color: "var(--app-muted)"}}>
          Profit Bersih = total yang Anda terima dari platform (setelah dipotong fee platform).
          Jastiper sudah memasukkan margin keuntungan ke dalam harga item, sehingga harga jual final
          mencerminkan semua biaya termasuk cuan Anda.
        </p>
      </div>

      {/* Top totals bento */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-0 border-t border-l" style={{borderColor: "var(--app-border)"}}>
        <FinanceStat
          icon={TrendingUp}
          label="Profit Bersih (Selesai)"
          value={formatIDR(totals.completed_profit || 0)}
          sub={`${totals.completed_count || 0} order selesai`}
          accent
          testid="stat-completed-profit"
        />
        <FinanceStat
          icon={Clock}
          label="Profit Akan Cair"
          value={formatIDR(totals.inflight_profit || 0)}
          sub={`${totals.inflight_count || 0} order berjalan`}
          testid="stat-inflight-profit"
        />
        {(totals.completed_service_fee || 0) > 0 && (
          <FinanceStat
            icon={Receipt}
            label="Cuan Jastip Mentah (legacy)"
            value={formatIDR(totals.completed_service_fee || 0)}
            sub="Sebelum potong platform"
            testid="stat-service-fee"
          />
        )}
        <FinanceStat
          icon={Wallet}
          label="Potongan Platform"
          value={formatIDR(totals.completed_platform_deduction || 0)}
          sub="Yang ditahan platform"
          testid="stat-platform-deduction"
        />
      </div>

      {/* Status tabs */}
      <div className="flex gap-0 border-b" style={{borderColor: "var(--app-border)"}}>
        {[
          { v: "completed", t: `Selesai (${totals.completed_count || 0})` },
          { v: "inflight", t: `Berjalan (${totals.inflight_count || 0})` },
          { v: "all", t: `Semua (${orders.length})` },
        ].map((opt) => (
          <button
            key={opt.v}
            onClick={() => setTab(opt.v)}
            className="px-5 py-3 text-sm font-medium border-b-2 transition-colors"
            style={{
              borderColor: tab === opt.v ? "var(--app-brand)" : "transparent",
              color: tab === opt.v ? "var(--app-brand)" : "var(--app-muted)",
            }}
            data-testid={`finance-tab-${opt.v}`}
          >
            {opt.t}
          </button>
        ))}
      </div>

      {/* Order list */}
      {filtered.length === 0 ? (
        <div className="border py-16 text-center" style={{borderColor: "var(--app-border)", backgroundColor: "var(--app-surface)"}}>
          <ShoppingBag size={32} strokeWidth={1.5} className="mx-auto mb-3" style={{color: "var(--app-muted)"}} />
          <p style={{color: "var(--app-muted)"}}>Belum ada order pada kategori ini.</p>
        </div>
      ) : (
        <div className="border" style={{borderColor: "var(--app-border)", backgroundColor: "var(--app-surface)"}} data-testid="finance-order-list">
          {filtered.map((o) => (
            <FinanceRow key={o.order_id} order={o} expanded={expanded.has(o.order_id)} onToggle={() => toggle(o.order_id)} />
          ))}
        </div>
      )}

      <div className="border p-4 flex items-start gap-3 text-sm" style={{borderColor: "var(--app-border)", backgroundColor: "var(--app-surface-alt)"}}>
        <Info size={18} strokeWidth={1.5} className="flex-shrink-0 mt-0.5" style={{color: "var(--app-brand)"}} />
        <div>
          <p style={{color: "var(--app-fg)"}}>
            <strong>Bedakan "Wire In" vs "Profit Bersih"</strong>: dana yang masuk ke Anda dari escrow ({formatIDR(totals.completed_wire || 0)} dari order selesai)
            sudah termasuk modal pembelian barang yang Anda keluarkan ke vendor.
          </p>
          <p className="mt-1" style={{color: "var(--app-muted)"}}>
            Yang benar-benar jadi cuan Anda adalah angka <strong>Profit Bersih</strong> di kiri atas.
          </p>
        </div>
      </div>
    </div>
  );
}

function FinanceStat({ icon: Icon, label, value, sub, accent, testid }) {
  return (
    <div
      className="border-r border-b p-6"
      style={{
        borderColor: "var(--app-border)",
        backgroundColor: accent ? "var(--app-brand)" : "var(--app-surface)",
        color: accent ? "#FFFFFF" : "var(--app-fg)",
      }}
      data-testid={testid}
    >
      <Icon size={20} strokeWidth={1.5} className="mb-3" style={{color: accent ? "rgba(255,255,255,0.85)" : "var(--app-brand)"}} />
      <div className="label-caps mb-2" style={{color: accent ? "rgba(255,255,255,0.75)" : "var(--app-muted)"}}>{label}</div>
      <div className="font-display text-xl tracking-tight">{value}</div>
      {sub && <div className="text-[11px] mt-1 font-mono" style={{color: accent ? "rgba(255,255,255,0.75)" : "var(--app-muted)"}}>{sub}</div>}
    </div>
  );
}

function FinanceRow({ order, expanded, onToggle }) {
  const statusPalette = {
    released:  { bg: "#DCFCE7", color: "#166534", label: "SELESAI" },
    paid:      { bg: "#FEF3C7", color: "#92400E", label: "DIBAYAR" },
    purchased: { bg: "#FEF3C7", color: "#92400E", label: "DIBELI" },
    shipping:  { bg: "#FEF3C7", color: "#92400E", label: "DIKIRIM" },
    delivered: { bg: "#FEF3C7", color: "#92400E", label: "SAMPAI" },
    cancelled: { bg: "#FEE2E2", color: "#991B1B", label: "DIBATALKAN" },
    refunded:  { bg: "#FEE2E2", color: "#991B1B", label: "REFUND" },
  };
  const s = statusPalette[order.status] || { bg: "var(--app-surface-alt)", color: "var(--app-muted)", label: order.status?.toUpperCase() || "—" };
  return (
    <div className="border-b last:border-b-0" style={{borderColor: "var(--app-border)"}} data-testid={`finance-row-${order.order_id}`}>
      {/* Compact header — clickable to expand */}
      <button
        type="button"
        onClick={onToggle}
        className="w-full text-left p-4 flex items-center gap-4 hover:bg-[var(--app-surface-alt)] transition-colors"
        data-testid={`finance-row-toggle-${order.order_id}`}
      >
        {expanded ? <ChevronDown size={16} strokeWidth={1.5} style={{color: "var(--app-muted)"}} /> : <ChevronRight size={16} strokeWidth={1.5} style={{color: "var(--app-muted)"}} />}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="label-caps px-2 py-0.5 font-mono text-[10px]" style={{backgroundColor: s.bg, color: s.color}}>
              {s.label}
            </span>
            <span className="font-mono text-xs" style={{color: "var(--app-muted)"}}>#{order.order_id?.slice(0,8)}</span>
            <span className="font-mono text-xs" style={{color: "var(--app-muted)"}}>{formatDate(order.created_at)}</span>
          </div>
          <div className="text-sm truncate">
            <span className="font-medium">{order.buyer_name}</span>
            <span className="ml-2" style={{color: "var(--app-muted)"}}>· {order.country}</span>
            <span className="ml-2" style={{color: "var(--app-muted)"}}>· {order.items?.length || 0} item</span>
          </div>
        </div>
        <div className="text-right hidden sm:block">
          <div className="text-[10px] uppercase tracking-wider" style={{color: "var(--app-muted)"}}>Profit Bersih</div>
          <div className="font-mono text-sm font-semibold" style={{color: order.bucket === "completed" ? "#059669" : "var(--app-muted)"}} data-testid={`finance-row-profit-${order.order_id}`}>
            {formatIDR(order.net_profit)}
          </div>
        </div>
      </button>

      {expanded && (
        <div className="px-4 pb-5 pl-12 space-y-4 text-sm" data-testid={`finance-row-detail-${order.order_id}`}>
          {/* Item list */}
          <div className="border" style={{borderColor: "var(--app-border)"}}>
            {(order.items || []).map((it, i) => {
              const fv = it.fee_value || 0;
              const itemFee = it.fee_type === "percent" ? (it.price * fv / 100) : fv;
              return (
                <div key={i} className="px-3 py-2 flex items-center justify-between border-b last:border-b-0" style={{borderColor: "var(--app-border)"}}>
                  <div className="flex items-center gap-2 min-w-0">
                    <Package size={12} strokeWidth={1.5} style={{color: "var(--app-muted)"}} />
                    <span className="truncate">{it.name}</span>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="font-mono text-xs">{formatIDR(it.price)}</div>
                    {fv > 0 && (
                      <div className="text-[10px] font-mono" style={{color: "#059669"}}>
                        + fee {it.fee_type === "percent" ? `${fv}%` : formatIDR(fv)} = {formatIDR(itemFee)}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Breakdown */}
          <div className="border p-3 space-y-1 font-mono text-xs" style={{borderColor: "var(--app-border)"}}>
            <BreakdownRow label="Modal barang (Anda belanjakan ke vendor)" value={formatIDR(order.item_total)} muted />
            {(order.service_fee || 0) > 0 && (
              <BreakdownRow label="Cuan jastip mentah Anda (legacy)" value={formatIDR(order.service_fee)} positive />
            )}
            <BreakdownRow label={`Potong platform (-${order.jastiper_platform_fee_percent}%)`} value={`-${formatIDR(order.jastiper_platform_fee)}`} negative />
            <div className="border-t pt-1 mt-1" style={{borderColor: "var(--app-border)"}}>
              <BreakdownRow label="🎯 PROFIT BERSIH ANDA" value={formatIDR(order.net_profit)} bold positive />
            </div>
          </div>

          <div className="text-[11px] font-mono space-y-0.5" style={{color: "var(--app-muted)"}}>
            <div>Buyer bayar total: <span style={{color: "var(--app-fg)"}}>{formatIDR(order.gross_amount)}</span></div>
            <div>Dana masuk ke Anda (incl. modal): <span style={{color: "var(--app-fg)"}}>{formatIDR(order.net_to_jastiper)}</span></div>
          </div>

          <Link
            to={`/orders/${order.order_id}`}
            className="inline-flex items-center gap-1 text-xs hover:underline"
            style={{color: "var(--app-brand)"}}
            data-testid={`finance-view-order-${order.order_id}`}
          >
            Lihat detail order <ArrowRight size={12} strokeWidth={1.5} />
          </Link>
        </div>
      )}
    </div>
  );
}

function BreakdownRow({ label, value, muted, positive, negative, bold }) {
  let color = "var(--app-fg)";
  if (muted) color = "var(--app-muted)";
  if (positive) color = "#059669";
  if (negative) color = "#DC2626";
  return (
    <div className="flex items-center justify-between gap-3">
      <span style={{color: muted ? "var(--app-muted)" : "var(--app-fg)", fontWeight: bold ? 600 : 400}}>{label}</span>
      <span style={{color, fontWeight: bold ? 700 : 500}}>{value}</span>
    </div>
  );
}
